import { useEffect, useState } from 'react';
import { Box, Button } from '@warp-ds/react';
import UserVerificationIcon from './UserVerification.js';
import Banner from '../Banner/index.js';
import pulseClient, { ObjectNameKycBox } from './pulse.js';
import { startKyc } from './api.js';
import { KycType } from './types.js';
import type { BannerType } from '../Banner/index.js';

type KycBoxProps = ConditionalKycBoxProps & {
    kyc?: KycType,
    banner?: BannerType,
    appBaseUrl: string,
    errorMessage: string,
    iconTitle: string,
};

type ConditionalKycBoxProps =
    | {
    entryPoint: 'AD_ADMIN';
    adId: string;
} | {
    entryPoint: 'TRANSACTION_OVERVIEW';
    adId?: never;
};

/**
 * Message asking seller to complete KYC or show a banner that KYC went successfully.
 */
const KycBox = (
    {
        kyc,
        banner,
        entryPoint,
        appBaseUrl,
        errorMessage,
        iconTitle,
        adId,
    }: KycBoxProps) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (kyc) {
            pulseClient.trackPageViewKycBox(ObjectNameKycBox.KYC_NOT_STARTED, entryPoint);
        }
        if (banner) {
            pulseClient.trackPageViewKycBox(ObjectNameKycBox.KYC_COMPLETED, entryPoint);
        }
    }, []);

    if (!kyc && !banner) {
        return null;
    }

    const redirectToKyc = async () => {
        setLoading(true);
        setError(null);
        pulseClient.trackStartButtonKycBox(entryPoint);

        startKyc(entryPoint, appBaseUrl, adId)
            .then((result) => {
                const { kycRedirectUrl } = result.data;
                window.location.href = kycRedirectUrl;
            })
            .catch(() => setError(errorMessage));
    };

    const mobileClassNames = 'lt-sm:flex-col lt-sm:items-center lt-sm:text-center';

    return (
        <>
            {kyc && (
                <Box info role="section" className={`p-20 flex gap-20 ${mobileClassNames}`}>
                    <UserVerificationIcon className="shrink-0" title={iconTitle} />
                    <div className={`flex flex-col gap-20 ${mobileClassNames}`}>

                        <h2 className="t4 m-0">{kyc.title}</h2>
                        <p className="m-0">{kyc.text}</p>

                        <Button onClick={redirectToKyc} primary loading={loading}>
                            {kyc.actions.startKyc.text}
                        </Button>
                        {error && <p className="s-text-negative">{error}</p>}
                    </div>
                </Box>)
            }
            {banner && <Banner banner={banner} />}
        </>
    );
};

export default KycBox;
